import { dentalPlans, dentalBenefits, dentalFaq } from '../../../mocks/dental';

const SITE = 'https://innoflextechnology.com';
const PAGE_URL = `${SITE}/odontologia`;

const ORG = {
  '@type': 'Organization',
  name: 'InnoFlex',
  url: SITE,
  telephone: '+18495388899',
  email: 'innoflextechnology@innoflexapp.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'C. Nicolás Ureña de Mendoza 55, Los Prados',
    addressLocality: 'Santo Domingo',
    addressRegion: 'Distrito Nacional',
    postalCode: '10131',
    addressCountry: 'DO',
  },
};

/* Rango de precios derivado de los planes, para que el schema no se desfase
   cuando cambian las tarifas. */
const MONTHLY_USD = dentalPlans.map((p) => p.monthly.USD);
const LOW_PRICE = Math.min(...MONTHLY_USD).toFixed(2);
const HIGH_PRICE = Math.max(...MONTHLY_USD).toFixed(2);

export default function DentalSEO() {
  const offers = dentalPlans.map((plan) => ({
    '@type': 'Offer',
    name: `InnoFlex Odontología ${plan.name}`,
    description: plan.description,
    price: plan.monthly.USD.toFixed(2),
    priceCurrency: 'USD',
    url: `${PAGE_URL}#planes-odontologia`,
    category: 'SubscriptionPlan',
    eligibleRegion: { '@type': 'Country', name: 'República Dominicana' },
  }));

  const softwareApp = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'InnoFlex Odontología',
    applicationCategory: 'HealthApplication',
    applicationSubCategory: 'Software para clínicas dentales',
    operatingSystem: 'Web, Android, iOS',
    url: PAGE_URL,
    description:
      'Plataforma integral para consultorios odontológicos, clínicas dentales y redes odontológicas: pacientes, agenda, historia clínica, odontograma, periodontograma, tratamientos, presupuestos, facturación, caja, inventario, laboratorios, seguros y ARS, portal del paciente y reportes.',
    author: ORG,
    featureList: dentalBenefits.map((b) => b.title),
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: LOW_PRICE,
      highPrice: HIGH_PRICE,
      offerCount: dentalPlans.length,
      offers,
    },
  };

  const product = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'InnoFlex Odontología',
    description:
      'Software para clínicas dentales en República Dominicana: agenda, expediente clínico, odontograma, periodontograma, tratamientos, presupuestos, cobros, inventario, seguros y ARS, y administración multisucursal.',
    brand: { '@type': 'Brand', name: 'InnoFlex' },
    category: 'Software para clínicas dentales',
    url: PAGE_URL,
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: LOW_PRICE,
      highPrice: HIGH_PRICE,
      offerCount: dentalPlans.length,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: dentalFaq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  const webPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': PAGE_URL,
    url: PAGE_URL,
    name: 'InnoFlex Odontología | Software para clínicas dentales',
    description:
      'Gestiona pacientes, agenda, odontograma, tratamientos, cobros, inventario, seguros y sucursales con InnoFlex Odontología.',
    inLanguage: 'es-DO',
    isPartOf: { '@type': 'WebSite', url: SITE, name: 'InnoFlex' },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Inicio', item: SITE },
        { '@type': 'ListItem', position: 2, name: 'Industrias', item: `${SITE}/industrias` },
        { '@type': 'ListItem', position: 3, name: 'Odontología', item: PAGE_URL },
      ],
    },
  };

  return (
    <>
      <title>InnoFlex Odontología | Software para clínicas dentales</title>
      <meta
        name="description"
        content="Gestiona pacientes, agenda, odontograma, tratamientos, cobros, inventario, seguros y sucursales con InnoFlex Odontología."
      />
      <meta
        name="keywords"
        content="software para clínicas dentales, software odontológico República Dominicana, programa para consultorio dental, odontograma digital, agenda odontológica, historia clínica odontológica, software dental Santo Domingo"
      />
      <link rel="canonical" href={PAGE_URL} />
      <meta property="og:title" content="InnoFlex Odontología | Software para clínicas dentales" />
      <meta
        property="og:description"
        content="Pacientes, agenda, historia clínica, odontograma, tratamientos, cobros, inventario, seguros y multisucursal en una sola plataforma."
      />
      <meta property="og:url" content={PAGE_URL} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="es_DO" />
      <script type="application/ld+json">{JSON.stringify(webPage)}</script>
      <script type="application/ld+json">{JSON.stringify(softwareApp)}</script>
      <script type="application/ld+json">{JSON.stringify(product)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </>
  );
}
